<script setup lang="ts">
/**
 * BG §19.1 — the display voice, and the only place Anton appears in the product.
 *
 * Anton is very condensed and very heavy. At heading-in-a-list sizes it gets cramped and costs
 * scanability, so it is reserved for the one title that names the page; every heading below this
 * is ZhSectionHeading. The kicker above it carries the operational context in mono.
 */
defineProps<{
    /** Optional mono line above the title: `TEAM`, `RUN 4291`, `ENTERPRISE`. */
    kicker?: string
}>()
</script>

<template>
    <header class="zh-page-title">
        <p v-if="kicker" class="zh-page-title__kicker">{{ kicker }}</p>
        <h1 class="zh-page-title__text"><slot /></h1>
        <p v-if="$slots.meta" class="zh-page-title__meta"><slot name="meta" /></p>
    </header>
</template>

<style scoped>
.zh-page-title {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.zh-page-title__kicker {
    margin: 0;
    font-family: var(--font-mono);
    font-size: 0.75rem;
    font-weight: 500;
    letter-spacing: var(--tracking-system);
    text-transform: uppercase;
    color: var(--electric-cyan);
}

.zh-page-title__text {
    margin: 0;
    font-family: var(--font-display);
    /* Uppercased in CSS so screen readers announce words, not initialisms. */
    text-transform: uppercase;
    font-size: clamp(1.5rem, 3.5vw, 2.125rem);
    font-weight: 400;
    line-height: 1.05;
    letter-spacing: var(--tracking-display);
    color: var(--dirty-white);
    /* Titles are often a team, task or member name, which can be long and unbroken. */
    overflow-wrap: anywhere;
}

.zh-page-title__meta {
    margin: 0;
    font-family: var(--font-mono);
    font-size: 0.75rem;
    color: var(--muted-grey);
}
</style>
